import { type ReactNode, useEffect, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import Animated, {
  Easing,
  type SharedValue,
  useAnimatedProps,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import { Rect, Svg } from "react-native-svg";
import { View, type ViewProps, getTokenValue, useTheme } from "tamagui";

/*
  Wraps a rounded element with an animated stroke: a teal comet travels around a
  neutral base ring. Dash lengths are fractions of the measured rounded-rect
  perimeter, so segment size and speed stay constant across any aspect ratio.
*/

const LAP_MS = 3200;
const TAIL_COPIES = 18; // trailing dash copies that form the feathered tail
const DASH_FRACTION = 0.06; // each copy's dash length, as a fraction of perimeter
const PHASE_FRACTION = 0.0125; // spacing between copies, as a fraction of perimeter
const AnimatedRect = Animated.createAnimatedComponent(Rect);

function roundedRectPerimeter(width: number, height: number, r: number) {
  const straightX = Math.max(0, width - 2 * r);
  const straightY = Math.max(0, height - 2 * r);
  return 2 * straightX + 2 * straightY + 2 * Math.PI * r;
}

type AnimatedStrokeBorderProps = ViewProps & {
  children: ReactNode;
  /** When false, children render untouched (no overlay). */
  enabled?: boolean;
  /** Radius token to match the wrapped element's border radius. */
  radius?: Parameters<typeof getTokenValue>[0];
  /** Resolved color for the static base ring. Defaults to $strokeNeutral. */
  baseColor?: string;
  /** Resolved color for the traveling comet. Defaults to $fillPrimary. */
  segmentColor?: string;
};

export default function AnimatedStrokeBorder({
  children,
  enabled = true,
  radius = "$150",
  baseColor: baseColorProp,
  segmentColor: segmentColorProp,
  ...viewProps
}: AnimatedStrokeBorderProps) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const offset = useSharedValue(0);

  // Inset the rect 1px so the stroke sits within the element's border band.
  const rx = Number(getTokenValue(radius)) - 1;
  const strokeWidth = Number(getTokenValue("$strokeWidth", "size"));
  const innerWidth = size.width - 2;
  const innerHeight = size.height - 2;
  const perimeter = roundedRectPerimeter(innerWidth, innerHeight, rx);
  const animate = enabled && !reducedMotion && perimeter > 0;

  useEffect(() => {
    if (!animate) {
      offset.value = 0;
      return;
    }
    // Travel one full perimeter per lap, forever. -perimeter ≡ 0 (the dash
    // pattern's period is the perimeter), so each lap loops seamlessly.
    offset.value = 0;
    offset.value = withRepeat(
      withTiming(-perimeter, { duration: LAP_MS, easing: Easing.linear }),
      -1,
      false,
    );
  }, [animate, perimeter, offset]);

  if (!enabled) return <>{children}</>;

  const baseColor = baseColorProp ?? theme.strokeNeutral?.get();
  const segmentColor = segmentColorProp ?? theme.fillPrimary?.get();
  const ready = size.width > 0 && size.height > 0;

  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setSize((prev) =>
      prev.width === width && prev.height === height ? prev : { width, height },
    );
  };

  return (
    <View position="relative" onLayout={onLayout} {...viewProps}>
      {children}
      {ready && (
        <View
          position="absolute"
          top={0}
          left={0}
          pointerEvents="none"
          aria-hidden
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <Svg width={size.width} height={size.height}>
            <Rect
              x={1}
              y={1}
              width={innerWidth}
              height={innerHeight}
              rx={rx}
              ry={rx}
              fill="none"
              stroke={baseColor}
              strokeWidth={strokeWidth}
            />
            {/* traveling comet: hard leading edge, feathered trailing tail */}
            {animate &&
              Array.from({ length: TAIL_COPIES + 1 }, (_, k) => (
                <CometSegment
                  key={k}
                  offset={offset}
                  phase={k * PHASE_FRACTION * perimeter}
                  opacity={1 - k / (TAIL_COPIES + 1)}
                  x={1}
                  y={1}
                  width={innerWidth}
                  height={innerHeight}
                  rx={rx}
                  dash={DASH_FRACTION * perimeter}
                  perimeter={perimeter}
                  color={segmentColor}
                  strokeWidth={strokeWidth}
                />
              ))}
          </Svg>
        </View>
      )}
    </View>
  );
}

type CometSegmentProps = {
  offset: SharedValue<number>;
  phase: number;
  opacity: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rx: number;
  dash: number;
  perimeter: number;
  color?: string;
  strokeWidth: number;
};

function CometSegment({
  offset,
  phase,
  opacity,
  x,
  y,
  width,
  height,
  rx,
  dash,
  perimeter,
  color,
  strokeWidth,
}: CometSegmentProps) {
  // Each copy trails BEHIND the head by `phase` (added, so trailing copies sit
  // at smaller path positions) — keeping the hard head as the leading edge.
  const animatedProps = useAnimatedProps(
    () => ({
      strokeDashoffset: offset.value + phase,
    }),
    [offset, phase],
  );

  return (
    <AnimatedRect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={rx}
      ry={rx}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="butt"
      strokeDasharray={[dash, perimeter - dash]}
      opacity={opacity}
      animatedProps={animatedProps}
    />
  );
}
